import { Capacitor } from '@capacitor/core'
import { initializeApp } from 'firebase/app';
import {
  browserLocalPersistence,
  getAuth,
  indexedDBLocalPersistence,
  initializeAuth,
  connectAuthEmulator,
  setPersistence,
} from 'firebase/auth';
import { 
  getFirestore, 
  connectFirestoreEmulator,
  initializeFirestore,
} from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';

const firebaseConfig = {
  apiKey:
    import.meta.env.VITE_FIREBASE_API_KEY ||
    'AIzaSyA2zDpWXQ0D7Y3BxlIdeblz523SjuJnCTE',
  authDomain:
    import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || 'grounded-fitness.firebaseapp.com',
  projectId:
    import.meta.env.VITE_FIREBASE_PROJECT_ID || 'grounded-fitness',
  storageBucket:
    import.meta.env.VITE_FIREBASE_STORAGE_BUCKET ||
    'grounded-fitness.firebasestorage.app',
  messagingSenderId:
    import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '1031387952686',
  appId:
    import.meta.env.VITE_FIREBASE_APP_ID ||
    '1:1031387952686:web:d7476ecbb223d5fada11bf',
  measurementId:
    import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || 'G-1VXD2B0DBZ',
};

// Initialize Firebase
export const app = initializeApp(firebaseConfig);

// Initialize Auth with proper persistence for native
const createAuth = () => {
  if (!Capacitor.isNativePlatform()) {
    return getAuth(app)
  }

  try {
    return initializeAuth(app, {
      persistence: indexedDBLocalPersistence,
    })
  } catch (error) {
    console.warn('[firebase] Falling back to getAuth() after initializeAuth():', error)
    return getAuth(app)
  }
}

// Initialize Firestore with long polling for native (WebView has websocket issues)
const createFirestore = () => {
  if (Capacitor.isNativePlatform()) {
    console.log('[firebase] Native platform detected - using long polling for Firestore (no persistence)');
    try {
      // For native: long polling required, NO persistence to avoid IndexedDB issues
      return initializeFirestore(app, {
        experimentalForceLongPolling: true,
      });
    } catch (error) {
      // Firestore may already be initialized
      console.warn('[firebase] initializeFirestore failed, using getFirestore:', error);
      return getFirestore(app);
    }
  }
  
  return getFirestore(app);
}

export const auth = createAuth()
export const db = createFirestore();
export const storage = getStorage(app);
export const functions = getFunctions(app);

// Set auth persistence to local (survives browser restarts)
if (!Capacitor.isNativePlatform()) {
  setPersistence(auth, browserLocalPersistence).catch((error) => {
    console.error('Error setting auth persistence:', error);
  });
}

// Connect to emulators when VITE_USE_EMULATORS is set
// Works in both dev mode AND preview mode (for e2e tests)
const useEmulators = import.meta.env.VITE_USE_EMULATORS === 'true';
if (useEmulators) {
  console.log('[firebase] Connecting to emulators...');
  connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, 'localhost', 8080);
  connectStorageEmulator(storage, 'localhost', 9199);
  connectFunctionsEmulator(functions, 'localhost', 5001);
  console.log('[firebase] Connected to emulators');
}
